import { Tooltip } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CardSkeleton } from "@/components/common/CardSkeleton";

const ANALYTICS_CARD_SUBTITLE_LINE_HEIGHT = "1rem";

interface AnalyticsCardProps {
  value: React.ReactNode;
  previousText: string | null;
  isPositive: boolean;
  title: string;
  subtitle: React.ReactNode;
  icon: React.ReactNode;
  pillText: string | null;
  pillTextAsChip?: boolean;
  showGradient?: boolean;
  countUp?: boolean; // enable count-up animation for numeric values
  countUpDurationMs?: number; // optional duration override
  disableFade?: boolean; // disable fade/slide animation on value changes
  subtitleLoading?: boolean;
  valueAdornment?: React.ReactNode;
  bottomAdornment?: React.ReactNode;
  onClick?: () => void;
  trend?: {
    status: "loading" | "unavailable" | "ready";
    percent?: number;
    absoluteChange?: number;
    direction?: "up" | "down" | "flat";
    isGood?: boolean;
    tooltip?: string;
    text?: string;
  };
}

const parseNumeric = (
  raw: React.ReactNode,
): { num: number | null; prefix: string; suffix: string; decimals: number } => {
  if (typeof raw === "number") {
    const decimals = (raw.toString().split(".")[1] || "").length;
    return { num: raw, prefix: "", suffix: "", decimals };
  }
  return { num: null, prefix: "", suffix: "", decimals: 0 };
};

const formatWithSeparators = (num: number, decimals: number, prefix = "", suffix = ""): string => {
  const rounded = decimals > 0 ? Number(num.toFixed(decimals)) : Math.round(num);
  const formatted = rounded.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${prefix}${formatted}${suffix}`;
};

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  value,
  previousText,
  isPositive: _isPositive,
  title,
  subtitle,
  icon,
  pillText,
  pillTextAsChip = false,
  showGradient = false,
  countUp = false,
  countUpDurationMs = 1000,
  disableFade = false,
  subtitleLoading = false,
  valueAdornment,
  bottomAdornment,
  onClick,
  trend,
}) => {
  const { t } = useTranslation();
  const isClickable = !!onClick;
  const topRightTextRaw = pillText ?? previousText ?? null;
  const isPill = Boolean(topRightTextRaw && (pillTextAsChip || !topRightTextRaw.includes("%")));

  // Count-up logic
  const { num, prefix, suffix, decimals } = useMemo(() => parseNumeric(value), [value]);
  const [display, setDisplay] = useState<React.ReactNode>(value);
  const prevNumRef = useRef<number | null>(num);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!countUp || num == null) {
      setDisplay(value);
      prevNumRef.current = num;
      return;
    }

    const start = prevNumRef.current == null ? 0 : prevNumRef.current;
    const end = num;
    const duration = Math.max(200, countUpDurationMs);
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic for smoother finish
      const eased = 1 - Math.pow(1 - t, 3);
      const current = start + (end - start) * eased;
      setDisplay(formatWithSeparators(current, decimals, prefix, suffix));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevNumRef.current = end;
      }
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [num, countUp, countUpDurationMs, decimals, prefix, suffix, value]);

  // Pre-format value (when not counting up) and top-right text
  const formattedStaticValue = useMemo(() => {
    if (countUp) return null;
    if (num == null) return value;
    return formatWithSeparators(num, decimals, prefix, suffix);
  }, [countUp, num, decimals, prefix, suffix, value]);

  const topRightText = useMemo(() => {
    if (!topRightTextRaw) return null;
    return topRightTextRaw;
  }, [topRightTextRaw]);

  const animatedValueKey = useMemo(() => {
    const renderedValue = countUp ? display : formattedStaticValue;
    if (typeof renderedValue === "string" || typeof renderedValue === "number") {
      return renderedValue;
    }
    return title;
  }, [countUp, display, formattedStaticValue, title]);

  const renderedSubtitle = subtitle;
  const hasSubtitle =
    typeof renderedSubtitle === "string"
      ? renderedSubtitle.trim().length > 0
      : renderedSubtitle !== null && renderedSubtitle !== undefined;
  const isSubtitleLoading = subtitleLoading;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick?.();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isClickable) return;

    // Keep click interaction without leaving the card in a focused state after pointer input.
    e.preventDefault();
  };

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      className={`relative rounded-2xl w-full h-full p-4 box-border transition-all duration-150 ease-out
      ${showGradient ? "bg-gradient-to-br from-[rgba(0,102,255,0.16)] to-[rgba(0,183,255,0.16)]" : "bg-white border border-blue-50 shadow-md shadow-blue-100"}
      ${
        isClickable
          ? `cursor-pointer border border-transparent hover:-translate-y-0.5 hover:shadow-lg ${
              showGradient ? "hover:border-[#BFDBFE]" : "hover:border-primary/30"
            } active:scale-[0.98] active:shadow-sm active:duration-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/70 focus-visible:ring-offset-2 group`
          : ""
      }
      flex flex-col`}
      data-name="Card - Primary Metric"
    >
      <div className="flex justify-between items-start w-full">
        <div className="flex items-center gap-3">
          <div
            className={`bg-gradient-to-r box-border flex from-[rgba(0,102,255,0.55)] to-[rgba(0,183,255,0.55)]
             items-center justify-center p-2 relative rounded-lg shrink-0 w-8 h-8 text-white transition-all duration-150
             ${isClickable ? "group-hover:from-[rgba(0,102,255,0.7)] group-hover:to-[rgba(0,183,255,0.7)]" : ""}`}
          >
            {icon}
          </div>
          <div className="inline-flex items-start gap-2" style={{ textAlign: "start" }}>
            <div className="font-bold text-3xl tracking-[0.24px]">
              {disableFade ? (
                <span className="block">{countUp ? display : formattedStaticValue}</span>
              ) : (
                <span key={animatedValueKey} className="value-change block">
                  {countUp ? display : formattedStaticValue}
                </span>
              )}
            </div>
            {valueAdornment && (
              <span className="relative top-[0.5em] text-xs flex leading-none">
                {valueAdornment}
              </span>
            )}
          </div>
        </div>

        <div
          className="flex min-h-6 shrink-0 flex-col items-end gap-1"
          style={{ textAlign: "end" }}
        >
          {topRightText && (
            <div
              className={`text-xs font-medium ${
                isPill
                  ? `inline-flex items-center shrink-0 rounded-full px-2.5 py-1 leading-none border ${
                      showGradient
                        ? "bg-white/85 text-slate-800 border-white/80 shadow-sm backdrop-blur-[2px]"
                        : "bg-[#dbe4ff] text-inherit border-blue-100/70 shadow-sm shadow-blue-100/70"
                    }`
                  : ""
              }`}
            >
              {topRightText}
            </div>
          )}
          {trend?.status === "loading" ? (
            <CardSkeleton
              tone={showGradient ? "blueCard" : "default"}
              width={46}
              sx={{
                height: "0.875rem",
                transform: "none",
              }}
            />
          ) : null}
          {trend?.status === "unavailable" ? (
            <span className="text-xs font-semibold text-slate-500" aria-hidden="true">
              –
            </span>
          ) : null}
          {trend?.status === "ready" ? (
            <Tooltip title={trend.tooltip ?? ""} slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}>
              <span
                className={`inline-flex items-center text-xs font-semibold ${
                  trend.direction === "flat"
                    ? "text-slate-500"
                    : trend.isGood
                      ? "text-emerald-600"
                      : "text-[#FF6717]"
                } ${
                  trend.direction === "up"
                    ? "before:me-0.5 before:text-[9px] before:leading-none before:content-['▲']"
                    : trend.direction === "down"
                      ? "before:me-0.5 before:text-[9px] before:leading-none before:content-['▼']"
                      : "before:me-0.5 before:text-[11px] before:leading-none before:content-['=']"
                }`}
                aria-label={trend.tooltip}
              >
                {trend.absoluteChange !== undefined && trend.absoluteChange > 0
                  ? t("common.analytics.trends.upFromZero")
                  : trend.text}
              </span>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <div className="mt-auto flex min-h-10 items-end justify-between gap-2">
        <div
          className={`flex flex-col ${hasSubtitle ? "justify-start" : "justify-center"}`}
          style={{ textAlign: "start" }}
        >
          <div className="font-semibold text-sm w-full">{title}</div>
          {hasSubtitle ? (
            <div
              className="font-normal text-xs w-full mt-1"
              style={{
                color: "grey",
                filter: "grayscale(100%)",
                lineHeight: ANALYTICS_CARD_SUBTITLE_LINE_HEIGHT,
              }}
            >
              {isSubtitleLoading ? (
                <CardSkeleton
                  tone={showGradient ? "blueCard" : "default"}
                  width="33%"
                  height="1em"
                  sx={{ transform: "none" }}
                />
              ) : (
                renderedSubtitle
              )}
            </div>
          ) : null}
        </div>
        {bottomAdornment && <div className="shrink-0">{bottomAdornment}</div>}
      </div>
    </div>
  );
};

export default AnalyticsCard;
